import React from "react";
import { CompanyVerificationStatus } from "../../types/companyVerification";

type Size = "sm" | "md" | "lg";

type Props = {
  status?: CompanyVerificationStatus;
  size?: Size;
  showText?: boolean; // allows compact usage
  className?: string;
};

const label = (s: CompanyVerificationStatus) => {
  switch (s) {
    case CompanyVerificationStatus.Verified:
      return "Verified";
    case CompanyVerificationStatus.Pending:
      return "Pending";
    case CompanyVerificationStatus.Rejected:
      return "Rejected";
    case CompanyVerificationStatus.Unverified:
    default:
      return "Unverified";
  }
};

const tone = (s: CompanyVerificationStatus) => {
  switch (s) {
    case CompanyVerificationStatus.Verified:
      return "bg-green-100 text-green-800 border-green-200";
    case CompanyVerificationStatus.Pending:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case CompanyVerificationStatus.Rejected:
      return "bg-red-100 text-red-800 border-red-200";
    case CompanyVerificationStatus.Unverified:
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const sizeClass = (size: Size) => {
  switch (size) {
    case "sm":
      return "px-2 py-0.5 text-xs";
    case "md":
      return "px-2.5 py-1 text-sm";
    case "lg":
      return "px-3 py-1.5 text-sm";
  }
};

export const CompanyVerificationBadge: React.FC<Props> = ({
  status,
  size = "sm",
  showText = true,
  className = "",
}) => {
  if (status === undefined) {
    return null;
  }
  const text = label(status);

  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border font-medium",
        tone(status),
        sizeClass(size),
        className,
      ].join(" ")}
      title={`Company verification: ${text}`}
      aria-label={`Company verification: ${text}`}
    >
      {showText ? text : null}
    </span>
  );
};
